import { useQuery } from '@tanstack/react-query';
import { parseEther } from 'ethers/lib/utils';

import { queryOptions } from '~components/hooks/_constants';
import {
  useContractCurveFactoryPool,
  useContractBalPool,
  useContractAsset,
} from '~components/hooks/contracts';
import { useSession } from '~components/hooks/useSession';
import { AddressString } from '~components/providers/types';
import { checkValidContractAddress, getFarmConfig } from '~config/protocol';
import { BalFarm, ELPType, VestaFarm } from '~config/types';

import { useSingleAssetPrices } from './useAssetPrices';

export function useVstFraxPrice() {
  const { network } = useSession();
  const { read: curveFactoryPoolRead, address: curveFactoryPoolAddress } = useContractCurveFactoryPool(
    ELPType['VST-FRAX'],
  );

  const res = useQuery<number>(
    ['vstfrax-lp-price', network.networkId],
    async () => {
      const [priceVstFrax] = await Promise.all([curveFactoryPoolRead.get_virtual_price()]).catch(() => [
        parseEther('1'),
      ]);
      return Number(priceVstFrax) / 10 ** 18;
    },
    queryOptions(true, checkValidContractAddress(curveFactoryPoolAddress)),
  );

  return { ...res, data: res.data ?? 0 };
}

export function useVstaWethPrice() {
  const { network } = useSession();
  const { read: balVaultRead, address: balVaultAddress } = useContractBalPool();
  const { read: vstEthERC20Read, address: vstEthERC20Address } = useContractAsset(ELPType['VSTA-ETH']);
  const { data: assetPrices, isFetched: isAssetsFetched } = useSingleAssetPrices();

  const res = useQuery<number>(
    ['vstaweth-lp-price', network.networkId],
    async () => {
      const [balancerPoolTokens, totalSupply] = await Promise.all([
        balVaultRead.getPoolTokens([getFarmConfig(VestaFarm['VSTA-ETH']).poolId as AddressString]),
        vstEthERC20Read.totalSupply(),
      ]).catch(err => {
        console.error(err);
        return [null, null];
      });
      if (!balancerPoolTokens || !totalSupply) return 0;

      const reserveWethUSD = (Number(balancerPoolTokens[1][0]) / 10 ** 18) * assetPrices.market.WETH;
      const reserveVstaUSD = (Number(balancerPoolTokens[1][1]) / 10 ** 18) * assetPrices.market.VSTA;
      const liquidity = reserveWethUSD + reserveVstaUSD;
      const supply = Number(totalSupply) / 10 ** 18;

      return liquidity / supply;
    },
    {
      retry: (failureCount: number) => {
        if (failureCount > 5) return false;
        return true;
      },
      retryDelay: 10000,
      refetchOnMount: false,
      refetchOnWindowFocus: 'always',
      enabled: isAssetsFetched && checkValidContractAddress([balVaultAddress, vstEthERC20Address]),
    },
  );

  return { ...res, data: res.data ?? 0 };
}

export function useVst4PoolPrice() {
  const { network } = useSession();
  const { read: balVaultRead, address: balVaultAddress } = useContractBalPool();
  const { read: vst4PoolERC20Read, address: vst4PoolERC20Address } = useContractAsset(
    ELPType['VST-USDC-DAI-USDT'],
  );
  const {
    data: assetPrices,
    isFetched: isAssetsFetched,
    isLoading: isPricesLoading,
  } = useSingleAssetPrices();

  const res = useQuery<number>(
    ['vst4pool-lp-price', network.networkId],
    async () => {
      const [balancerPoolTokens, totalSupply] = await Promise.all([
        balVaultRead.getPoolTokens([getFarmConfig(BalFarm['VST-USDC-DAI-USDT']).poolId as AddressString]),
        vst4PoolERC20Read.totalSupply(),
      ]).catch(err => {
        console.error(err);
        return [null, null];
      });

      if (!balancerPoolTokens || !totalSupply) return 0;

      const reserveVst = (Number(balancerPoolTokens[1][0]) / 10 ** 18) * assetPrices.market.VST;
      const reserveUsdc = (Number(balancerPoolTokens[1][1]) / 10 ** 18) * assetPrices.market.DAI;
      const reserveDai = (Number(balancerPoolTokens[1][2]) / 10 ** 6) * assetPrices.market.USDT;
      const reserveUsdt = (Number(balancerPoolTokens[1][3]) / 10 ** 6) * assetPrices.market.USDC;
      const liquidity = reserveVst + reserveUsdc + reserveDai + reserveUsdt;
      const supply = Number(totalSupply) / 10 ** 18;

      return liquidity / supply;
    },
    {
      retry: (failureCount: number) => {
        if (failureCount > 5) return false;
        return true;
      },
      retryDelay: 10000,
      refetchOnMount: false,
      refetchOnWindowFocus: 'always',
      enabled:
        isAssetsFetched &&
        !isPricesLoading &&
        checkValidContractAddress([balVaultAddress, vst4PoolERC20Address]),
    },
  );

  return { ...res, data: res.data ?? 0 };
}
